import { useApiClient } from "@positive/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";

import { createAccess, noAccess, type Access } from "./access";
import type { AuthService, AuthState } from "./authService";
import type { HostMode } from "./host";
import { fetchSession, sessionQueryKey, switchSessionContext, type SessionContext } from "./session";
import { realmRoles } from "./token";

export interface Identity {
  auth: AuthService;
  mode: HostMode;
  authState: AuthState;
  /** `/api/platform/me` of a tenant user; `null` in the operator console. */
  session: SessionContext | null;
  access: Access;
  /** Name shown in the Shell Bar. */
  displayName: string;
  /** Operator support session on this tenant host (A3), if any. */
  supportSessionId: string | null;
  /** Switches company/branch server-side (P12) and reloads everything that depends on it. */
  switchContext: (selection: { companyId: string; branchId: string; allCompanies?: boolean }) => Promise<void>;
}

interface IdentityState {
  identity: Identity;
  /** `/me` request state (tenant mode only). */
  sessionStatus: "idle" | "pending" | "error" | "success";
  sessionError: unknown;
  retrySession: () => void;
}

const IdentityContext = createContext<IdentityState | null>(null);

export interface IdentityProviderProps {
  auth: AuthService;
  mode: HostMode;
  supportSessionId: string | null;
  children: ReactNode;
}

/**
 * Identity and session bootstrap: OIDC state from `AuthService`, then — on a tenant host — `GET /api/platform/me`
 * (user, tenant, companies/branches, session branch, permissions, scopes, idle policy, locale). In the operator
 * console the realm role `platform_operator` from the token decides access.
 */
export function IdentityProvider({ auth, mode, supportSessionId, children }: IdentityProviderProps) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const authState = useSyncExternalStore(auth.subscribe, auth.getState);
  const authenticated = authState.status === "authenticated";

  const me = useQuery({
    queryKey: sessionQueryKey,
    queryFn: ({ signal }) => fetchSession(client, signal),
    enabled: authenticated && mode.kind === "tenant",
    staleTime: Infinity,
  });

  const switchContext = useCallback(
    async (selection: { companyId: string; branchId: string; allCompanies?: boolean }) => {
      await switchSessionContext(client, selection);
      // Permissions are per company and every list depends on the branch: reload all server state.
      await queryClient.invalidateQueries();
    },
    [client, queryClient],
  );

  const roles = useMemo(() => (authenticated ? authState.roles : []), [authState, authenticated]);
  const profile = authenticated ? authState.user.profile : null;
  const session = mode.kind === "tenant" ? (me.data ?? null) : null;

  const access = useMemo<Access>(() => {
    if (mode.kind === "console") {
      return roles.includes(realmRoles.platformOperator)
        ? createAccess({ permissions: [], modules: [], isOperator: true, isTenantUser: false })
        : noAccess;
    }
    return session ? createAccess({ permissions: session.permissions, modules: session.tenant.modules }) : noAccess;
  }, [mode.kind, roles, session]);

  const value = useMemo<IdentityState>(
    () => ({
      identity: {
        auth,
        mode,
        authState,
        session,
        access,
        displayName: session?.user.displayName ?? profile?.name ?? profile?.preferred_username ?? "",
        supportSessionId,
        switchContext,
      },
      sessionStatus: mode.kind === "console" || !authenticated ? "idle" : me.status,
      sessionError: me.error,
      retrySession: () => void me.refetch(),
    }),
    [access, auth, authState, authenticated, me, mode, profile, session, supportSessionId, switchContext],
  );

  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}

function useIdentityState(): IdentityState {
  const value = useContext(IdentityContext);
  if (!value) throw new Error("useIdentity must be used inside IdentityProvider");
  return value;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useIdentity(): Identity {
  return useIdentityState().identity;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useIdentityBootstrap(): Omit<IdentityState, "identity"> & { identity: Identity } {
  return useIdentityState();
}

/** Effective access of the signed-in user (deny by default). */
// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useAccess(): Access {
  return useIdentityState().identity.access;
}

/** The tenant session; only valid inside the tenant app after bootstrap. */
// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useSession(): SessionContext {
  const session = useIdentityState().identity.session;
  if (!session) throw new Error("useSession requires a loaded tenant session");
  return session;
}
